import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { dateFormatter, nestedFieldValueGetter } from 'utils/grid';

import { GridColDef } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { Chip } from '@mui/material';
import {
  BLUE,
  BLUE_TERTIARY,
  ORANGE_LIGHT,
  ORANGE_SECONDARY,
  PRIMARY,
  PRIMARY_LIGHT,
  RED,
  RED_LIGHT
} from 'constants/colors';
import { StatusFilter } from 'components/employee-portal/leave-requests/components/StatusFilter';

type Row = {
  [key: string]: any;
};

const detailsValueGetter = (field: string, row: { [key: string]: any }) =>
  field.split('.').reduce((value, part) => value && value[part], row);

const dutyTimeValueGetter = (row: Row) => {
  const startTime = row?.request?.duty?.dutyStartTime;
  const endTime = row?.request?.duty?.dutyEndTime;

  if (startTime && endTime) {
    const formattedStartTime = dayjs(startTime).format('HH:mm');
    const formattedEndTime = dayjs(endTime).format('HH:mm');
    return `${formattedStartTime} - ${formattedEndTime}`;
  }
  return '';
};

const status = {
  unlinked: {
    label: 'flexHistory.unlinked',
    style: { backgroundColor: ORANGE_LIGHT, color: ORANGE_SECONDARY }
  },
  withdrawn: {
    label: 'flexHistory.withdrawn',
    style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
  },
  rejected: {
    label: 'flexHistory.rejected',
    style: { backgroundColor: RED_LIGHT, color: RED }
  },
  requestUpdated: {
    label: 'flexHistory.updated',
    style: { backgroundColor: BLUE_TERTIARY, color: BLUE }
  },
  default: {
    label: 'flexHistory.linked',
    style: { backgroundColor: PRIMARY_LIGHT, color: PRIMARY }
  }
};

const renderDeleteReasonCell = (params, t) => {
  const value = params.value;
  const statusConfig = status[value] || status.default;
  return (
    <Chip
      label={t(statusConfig.label)}
      size="small"
      style={statusConfig.style}
    />
  );
};

export const useGetColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        {
          field: 'request.requestId',
          type: 'string',
          headerName: t('flexHistory.number'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter('request.requestId', row),
          flex: 1,
          maxWidth: 75
        },
        {
          field: 'request.costCenter.name',
          type: 'string',
          headerName: t('planUnit'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter('request.costCenter.name', row),
          flex: 1,
          minWidth: 100
        },
        {
          field: 'request.date',
          type: 'date',
          headerName: t('date'),
          valueGetter: (_, row: Row) =>
            nestedFieldValueGetter('request.date', row),
          valueFormatter: dateFormatter,
          flex: 1
        },
        {
          field: 'request.duty.dutyEndTime',
          type: 'string',
          headerName: t('time'),
          valueGetter: (_, row: Row) => dutyTimeValueGetter(row),
          flex: 1
        },
        {
          field: 'request.duty.code',
          type: 'string',
          headerName: t('dutyCode'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter('request.duty.code', row),
          flex: 1,
          maxWidth: 80
        },
        {
          field: 'request.function.description',
          type: 'string',
          headerName: t('function'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter('request.function.description', row),
          flex: 1
        },
        {
          field: 'dutyLine.employee.nameCompound',
          type: 'string',
          headerName: t('employees'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter('dutyLine.employee.nameCompound', row),
          flex: 1
        },
        {
          field: 'dutyLine.employee.employeeFunctionDescription',
          type: 'string',
          headerName: t('flexHistory.employeeFunction'),
          valueGetter: (_, row: Row) =>
            detailsValueGetter(
              'dutyLine.employee.employeeFunctionDescription',
              row
            ),
          flex: 1
        },
        {
          field: 'deleteReason',
          type: 'string',
          headerName: t('flexHistory.status'),
          valueGetter: (_, row: Row) => detailsValueGetter('deleteReason', row),
          minWidth: 110,
          renderCell: (params) => renderDeleteReasonCell(params, t),
          filterOperators: [
            {
              label: t('flexHistory.equals'),
              value: 'equals',
              getApplyFilterFn: (filterItem) => {
                return (row) => (row.value || 'default') === filterItem.value;
              },
              InputComponent: (props) => (
                <StatusFilter {...props} status={status} />
              )
            }
          ]
        }
      ] as GridColDef[],
    [t]
  );
};
